import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * shadcn-style Badge on the Coreway tokens. Meaning comes from the semantic pairs (design/tokens.json
 * `urgency.*`, the same values as `fleet.*`): success = ok, warning = due soon, danger = overdue or
 * expired, muted = off or missing, info = neutral news. `brand` (the accent wash) is for product news
 * only and never carries meaning, so it is never a default: a caller has to ask for it by name.
 *
 * Every badge says what it means in words (`label` is required and may not be blank); the tone and the
 * optional Lucide icon only reinforce it (design-tokens.md "every chip has a label"). The existing chips
 * (DocStatusChip, FleetStatusChip, OwnerStatementStatusChip) stay as they are.
 */
export const BADGE_TONES = ['success', 'warning', 'danger', 'info', 'muted', 'brand'] as const;
export type BadgeTone = (typeof BADGE_TONES)[number];

const TONE: Record<BadgeTone, string> = {
  success: 'bg-urgency-ok-bg text-urgency-ok-fg',
  warning: 'bg-urgency-warning-bg text-urgency-warning-fg',
  danger: 'bg-urgency-critical-bg text-urgency-critical-fg',
  info: 'bg-sunken text-info',
  muted: 'bg-urgency-missing-bg text-urgency-missing-fg',
  brand: 'bg-accent-wash text-accent-base',
};

const SIZE = {
  sm: 'gap-1 px-1.5 py-0.5 text-xs [&>svg]:size-3',
  md: 'gap-1 px-2 py-0.5 text-sm [&>svg]:size-3.5',
} as const;

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  label: string;
  tone?: BadgeTone;
  size?: keyof typeof SIZE;
  icon?: LucideIcon;
}

export function Badge({ label, tone = 'muted', size = 'md', icon: Icon, className, ...props }: BadgeProps) {
  if (label.trim() === '') throw new Error('Badge needs a visible label: the tone never carries the meaning alone.');
  return (
    <span
      data-slot="badge"
      data-tone={tone}
      data-size={size}
      className={cn(
        'tabular-figures inline-flex w-fit shrink-0 items-center rounded-md font-semibold whitespace-nowrap',
        TONE[tone],
        SIZE[size],
        className,
      )}
      {...props}
    >
      {Icon && <Icon className="shrink-0" aria-hidden="true" />}
      <span>{label}</span>
    </span>
  );
}
